// Site-wide display currency (Sep 2026 — USD-first pricing, Option A).
// AWG stays the authoritative pricing + settlement currency: every amount
// passed in here is the existing AWG value. USD is presentation only —
// either the approved clean list price (`usd` arg / item.price_usd) or the
// exact conversion of the AWG amount at the CMS rate (1.79).
import { useSyncExternalStore } from "react";
import { formatAWG, getAwgPerUsd } from "@/lib/api";

const KEY = "ur_display_currency";
const listeners = new Set();

const read = () => {
  try {
    return window.localStorage.getItem(KEY) === "awg" ? "awg" : "usd";
  } catch (_) {
    return "usd";
  }
};
let current = typeof window !== "undefined" ? read() : "usd";

export const getCurrency = () => current;
export const setCurrency = (c) => {
  current = c === "awg" ? "awg" : "usd";
  try { window.localStorage.setItem(KEY, current); } catch (_) { /* private mode */ }
  listeners.forEach((l) => l());
};
const subscribe = (l) => { listeners.add(l); return () => listeners.delete(l); };
export const useCurrency = () => useSyncExternalStore(subscribe, getCurrency, () => "usd");

// Internal tools (admin, designer) always show the authoritative AWG.
const isInternal = () =>
  typeof window !== "undefined" && /^\/(admin|designer)(\/|$)/.test(window.location.pathname);
export const isUSD = () => current === "usd" && !isInternal();
const isPrerender = () => typeof window !== "undefined" && window.__PRERENDER_SEO__;

const fmt = (n, decimals) =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);

export const awgToUsd = (awg) => Number(awg || 0) / getAwgPerUsd();
export const formatAwgExact = (awg) => {
  const n = Number(awg || 0);
  return `AWG ${Number.isInteger(n) ? fmt(n, 0) : fmt(n, 2)}`;
};

// Primary price string. `usd` = approved clean list price (only for single
// catalog items). Without it, USD mode shows the exact AWG conversion with
// cents, so totals always reconcile with the AWG amount actually charged.
export const money = (awg, usd) => {
  if (isPrerender()) return "";
  if (!isUSD()) return `AWG ${formatAWG(awg)}`;
  if (Number(usd) > 0) return `US$ ${fmt(Number(usd), 0)}`;
  const v = awgToUsd(awg);
  return `US$ ${fmt(v, Number.isInteger(Math.round(v * 100) / 100) ? 0 : 2)}`;
};

// Catalog-card price (single offering): approved clean US$ list price
// when set, otherwise the whole-dollar rate conversion. Never used for
// totals — totals use money() so they reconcile with the AWG charge.
export const listMoney = (awg, usd) => {
  if (isPrerender()) return "";
  if (!isUSD()) return `AWG ${formatAWG(awg)}`;
  const v = Number(usd) > 0 ? Number(usd) : Math.round(awgToUsd(awg));
  return `US$ ${fmt(v, 0)}`;
};

// Secondary line under a price — the other currency.
export const usdApprox = (awg) => {
  if (isPrerender()) return "";
  return isUSD() ? formatAwgExact(awg) : `≈ US$ ${fmt(Math.round(awgToUsd(awg)), 0)}`;
};

// US$ equivalent of an AWG amount (exact conversion, cents trimmed).
export const usdEquivalent = (awg) => {
  if (isPrerender()) return "";
  const v = awgToUsd(awg);
  const r2 = Math.round(v * 100) / 100;
  return `≈ US$ ${fmt(r2, Number.isInteger(r2) ? 0 : 2)}`;
};

// Scales an item's approved USD list price to a computed AWG amount
// (quantity multiples). Returns undefined → caller falls back to conversion.
export const usdFor = (item, awg, priceKey = "price", usdKey = "price_usd") => {
  const base = Number(item?.[priceKey] || 0);
  const usd = Number(item?.[usdKey] || 0);
  const amt = Number(awg || 0);
  if (!(base > 0 && usd > 0 && amt > 0)) return undefined;
  const q = amt / base;
  return Number.isInteger(q) ? usd * q : undefined;
};
